import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';
import Button from '../common/Button';
import { getCroppedImg } from '../../utils/imageCropUtils';

/**
 * Reusable image crop modal. Use for club banners, avatars, club photos, etc.
 *
 * Flow: parent provides image (as File or URL); modal shows crop UI with configurable
 * aspect ratio; on confirm, calls onConfirm(croppedBlob).
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {() => void} onClose - Called when user cancels or closes
 * @param {File|string|null} image - Image to crop (File or object URL string). When null/undefined, modal shows nothing useful
 * @param {(blob: Blob) => void} onConfirm - Called with cropped image Blob when user confirms
 * @param {number} [aspect=1] - Crop aspect ratio (width/height). e.g. 3 for banner (3:1), 1 for square (avatar)
 * @param {'rect'|'round'} [cropShape='rect'] - Shape of crop area: 'rect' for rectangles/banners, 'round' for circular avatars
 * @param {string} [title='Crop image'] - Modal title
 * @param {string} [confirmLabel='Use this image'] - Confirm button label
 * @param {Object} [outputOptions] - Passed to getCroppedImg: { format, quality, maxWidth, maxHeight }
 */
const ImageCropModal = ({
  isOpen,
  onClose,
  image,
  onConfirm,
  aspect = 1,
  cropShape = 'rect',
  title = 'Crop image',
  confirmLabel = 'Use this image',
  outputOptions = {}
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropError, setCropError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  // Resolve image to URL: string as-is; File → create object URL and revoke on close
  useEffect(() => {
    if (!isOpen) {
      setImageUrl(null);
      return;
    }
    if (!image) {
      setImageUrl(null);
      return;
    }
    if (typeof image === 'string') {
      setImageUrl(image);
      return () => {};
    }
    if (image instanceof File) {
      const url = URL.createObjectURL(image);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setImageUrl(null);
    return () => {};
  }, [isOpen, image]);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!imageUrl || !croppedAreaPixels) return;
    setIsCropping(true);
    setCropError(null);
    try {
      const blob = await getCroppedImg(imageUrl, croppedAreaPixels, {
        format: 'image/jpeg',
        quality: 0.9,
        ...outputOptions
      });
      onConfirm(blob);
      onClose();
    } catch (err) {
      console.error('Crop failed:', err);
      setCropError(err.message || 'Failed to crop image');
    } finally {
      setIsCropping(false);
    }
  }, [imageUrl, croppedAreaPixels, onConfirm, onClose, outputOptions]);

  const handleClose = useCallback(() => {
    setCropError(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnBackdropClick={true} closeOnEscape={true}>
      <ModalContent size="large" className="max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          <ModalCloseButton onClick={handleClose} />
        </div>

        {!imageUrl ? (
          <p className="text-sm text-text-secondary py-8 text-center">No image to crop.</p>
        ) : (
          <>
            <div className="relative w-full h-[min(60vh,400px)] bg-background rounded-lg overflow-hidden">
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                cropShape={cropShape}
                showGrid={cropShape === 'rect'}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="contain"
                style={{ containerStyle: { backgroundColor: '#f3f4f6' } }}
              />
            </div>

            {/* Zoom and actions bar - clearly separated */}
            <div className="mt-4 pt-4 border-t border-border bg-background rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1 w-full sm:max-w-sm">
                <label className="text-sm font-medium text-text-primary whitespace-nowrap shrink-0">
                  Zoom
                </label>
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-xs font-medium text-text-muted w-6">1×</span>
                  <input
                    type="range"
                    min={1}
                    max={5}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="flex-1 h-3 rounded-full appearance-none cursor-pointer bg-border [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0 accent-primary"
                    aria-label="Zoom"
                  />
                  <span className="text-xs font-medium text-text-muted w-6">5×</span>
                </div>
              </div>
              <div className="flex gap-3 shrink-0 border-t border-border pt-3 sm:border-t-0 sm:pt-0 sm:border-l sm:border-border sm:pl-4">
                <Button variant="secondary" size="medium" onClick={handleClose} disabled={isCropping}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handleConfirm}
                  disabled={isCropping || !croppedAreaPixels}
                >
                  {isCropping ? 'Cropping...' : confirmLabel}
                </Button>
              </div>
            </div>

            {cropError && (
              <p className="mt-3 text-sm text-danger-dark bg-danger-light border border-danger rounded-md px-3 py-2">
                {cropError}
              </p>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ImageCropModal;
